import React from 'react';
import { create } from 'zustand';
import { searchInitialQueryState } from 'utility/functions';
import {
  EditorialAssetSearchResultItem,
  EditorialSearchQueryParams,
} from 'utility/types/dataProvider';

/**
 * Media Library (Asset Library) - Zustand Store
 * see also: MediaLibContextProvider.tsx
 */
export interface IMediaLibStoreZustand {
  selectedRecords: EditorialAssetSearchResultItem[];
  setSelectedRecords: (
    value:
      | EditorialAssetSearchResultItem[]
      | ((
          prevState: EditorialAssetSearchResultItem[],
        ) => EditorialAssetSearchResultItem[]),
  ) => void;
  onClickInsert?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  setOnClickInsert: (
    fn?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
  ) => void;
  mediaLibDragging: boolean;
  setMediaLibDragging: (value: boolean) => void;
  queryState: EditorialSearchQueryParams;
  setQueryState: (
    value:
      | EditorialSearchQueryParams
      | ((prevState: EditorialSearchQueryParams) => EditorialSearchQueryParams),
  ) => void;
  replaceNode?: number | undefined;
  setReplaceNode: (value: number | undefined) => void;

  relatedDisplay: 'children' | 'siblings' | 'parents' | 'related_content';
  setRelatedDisplay: React.Dispatch<
    React.SetStateAction<'children' | 'siblings' | 'parents' | 'related_content'>
  >;

  relatedChildren: EditorialAssetSearchResultItem[];
  setRelatedChildren: (value: EditorialAssetSearchResultItem[]) => void;
  relatedSiblings: EditorialAssetSearchResultItem[];
  setRelatedSiblings: (value: EditorialAssetSearchResultItem[]) => void;
  relatedParents: EditorialAssetSearchResultItem[];
  setRelatedParents: (value: EditorialAssetSearchResultItem[]) => void;
  relatedContent: EditorialAssetSearchResultItem[]; // Assuming relatedContent is defined in the context
  setRelatedContent: (value: EditorialAssetSearchResultItem[]) => void;
  pendingChildren: EditorialAssetSearchResultItem[];
  setPendingChildren: (value: EditorialAssetSearchResultItem[]) => void;

  relatedAssetsFilterText: string;
  setRelatedAssetsFilterText: (value: string) => void;
}

export const useMediaLibStore = create<IMediaLibStoreZustand>()((set, get) => ({
  selectedRecords: [], // Initial state.
  setSelectedRecords: (value) => {
    set((state) => {
      const newSelectedRecords =
        typeof value === 'function' ? value(state.selectedRecords) : value;
      return {
        selectedRecords: newSelectedRecords,
      };
    });
  }, // Set selected records directly or via a function that has access to the previous state (like react)
  onClickInsert: undefined,
  setOnClickInsert: (fn) => set({ onClickInsert: fn }),
  mediaLibDragging: false, // Initial state, not dragging
  setMediaLibDragging: (value) => set({ mediaLibDragging: value }), // Set by MediaLibDisplayItem onDragEnd / onDragStart
  queryState: searchInitialQueryState, // Initial state
  setQueryState: (value) =>
    set((state) => ({
      queryState: typeof value === 'function' ? value(state.queryState) : value,
    })), // Set query state directly or via a function that has access to the previous state (like react)
  replaceNode: undefined,
  setReplaceNode: (value) => set({ replaceNode: value }),

  relatedDisplay: 'parents',
  setRelatedDisplay: (value) =>
    set((state) => ({
      relatedDisplay: typeof value === 'function' ? value(state.relatedDisplay) : value,
    })),

  relatedChildren: [],
  setRelatedChildren: (value) => set({ relatedChildren: value }),
  relatedSiblings: [],
  setRelatedSiblings: (value) => set({ relatedSiblings: value }),
  relatedParents: [],
  setRelatedParents: (value) => set({ relatedParents: value }),
  relatedContent: [],
  setRelatedContent: (value) => set({ relatedContent: value }),
  pendingChildren: [], // Initialize pendingChildren state
  setPendingChildren: (value) => set({ pendingChildren: value }),

  relatedAssetsFilterText: '',
  setRelatedAssetsFilterText: (value) => set({ relatedAssetsFilterText: value }),
}));
